import { getProcess, NEGOTIATION_PROCESS_NAME } from '../../transactions/transaction';
import {
  NEGOTIATION_SUPPORTED_PROCESS_VERSIONS,
  isNegotiationRelease3CompatibleProcessVersion,
} from '../../transactions/negotiationProcess';

export const INBOX_STATUS_ALL = 'all';
export const INBOX_STATUS_IN_PROCESS = 'in-process';
export const INBOX_STATUS_COMPLETED = 'completed';
export const INBOX_STATUS_CANCELED = 'canceled';
export const INBOX_STATUS_REJECTED = 'rejected';
export const INBOX_STATUS_EXPIRED = 'expired';
export const INBOX_STATUS_ADMINISTRATIVE_REVIEW = 'administrative-review';

export const INBOX_STATUS_CATEGORIES = [
  INBOX_STATUS_IN_PROCESS,
  INBOX_STATUS_COMPLETED,
  INBOX_STATUS_CANCELED,
  INBOX_STATUS_REJECTED,
  INBOX_STATUS_EXPIRED,
  INBOX_STATUS_ADMINISTRATIVE_REVIEW,
];

const createProcessStateCategories = () => ({
  'default-booking': {
    [INBOX_STATUS_IN_PROCESS]: ['inquiry', 'pending-payment', 'preauthorized', 'accepted'],
    [INBOX_STATUS_COMPLETED]: [
      'delivered',
      'reviewed-by-customer',
      'reviewed-by-provider',
      'reviewed',
    ],
    [INBOX_STATUS_CANCELED]: ['canceled', 'cancelled'],
    [INBOX_STATUS_REJECTED]: ['declined'],
    [INBOX_STATUS_EXPIRED]: ['payment-expired', 'expired'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: [],
  },
  'default-inquiry': {
    [INBOX_STATUS_IN_PROCESS]: ['free-inquiry'],
    [INBOX_STATUS_COMPLETED]: [],
    [INBOX_STATUS_CANCELED]: [],
    [INBOX_STATUS_REJECTED]: [],
    [INBOX_STATUS_EXPIRED]: [],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: [],
  },
  'default-negotiation': {
    [INBOX_STATUS_IN_PROCESS]: [
      'inquiry',
      'quote-requested',
      'offer-pending',
      'update-pending',
      'customer-offer-pending',
      'pending-payment',
      'offer-accepted',
      'delivered',
      'changes-requested',
      'provider-completed',
      'problem-reported',
      'provider-correction-completed',
    ],
    [INBOX_STATUS_COMPLETED]: [
      'completed',
      'reviewed-by-customer',
      'reviewed-by-provider',
      'reviewed',
      'completion-paid-to-provider',
    ],
    [INBOX_STATUS_CANCELED]: [
      'canceled',
      'cancellation-refunded',
      'cancellation-paid-to-provider',
      'partially-refunded',
      'completion-refunded',
    ],
    [INBOX_STATUS_REJECTED]: ['request-rejected', 'offer-rejected'],
    [INBOX_STATUS_EXPIRED]: ['payment-expired', 'provider-completion-expired'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: ['cancellation-review', 'completion-review'],
  },
  'default-purchase': {
    [INBOX_STATUS_IN_PROCESS]: ['inquiry', 'pending-payment', 'purchased', 'delivered', 'received'],
    [INBOX_STATUS_COMPLETED]: [
      'completed',
      'reviewed-by-customer',
      'reviewed-by-provider',
      'reviewed',
    ],
    [INBOX_STATUS_CANCELED]: ['canceled'],
    [INBOX_STATUS_REJECTED]: [],
    [INBOX_STATUS_EXPIRED]: ['payment-expired'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: ['disputed'],
  },
  'resuelvepr-booking': {
    [INBOX_STATUS_IN_PROCESS]: [
      'inquiry',
      'pending-payment',
      'pending-location-seal',
      'preauthorized',
      'accepted',
      'customer-reschedule-pending',
      'provider-reschedule-pending',
      'confirmation-open',
      'customer-confirmed',
      'provider-confirmed',
      'both-confirmed',
      'provider-completed',
      'work-pending',
      'problem-reported',
      'provider-completed-final',
      'provider-correction-completed',
    ],
    [INBOX_STATUS_COMPLETED]: [
      'delivered',
      'reviewed-by-customer',
      'reviewed-by-provider',
      'reviewed',
      'completion-paid-to-provider',
    ],
    [INBOX_STATUS_CANCELED]: [
      'canceled',
      'cancelled',
      'canceled-before-confirmation',
      'cancellation-refunded',
      'completion-refunded',
      'partially-refunded',
      'cancellation-paid-to-provider',
    ],
    [INBOX_STATUS_REJECTED]: ['declined'],
    [INBOX_STATUS_EXPIRED]: ['payment-expired', 'expired', 'provider-completion-expired'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: [
      'manual-review',
      'cancellation-review',
      'completion-review',
    ],
  },
});

// Keep tab-specific configurations independent even though their current classifications match.
const processStateCategoriesByTab = {
  orders: createProcessStateCategories(),
  sales: createProcessStateCategories(),
};

export const getValidInboxStatus = status =>
  status === INBOX_STATUS_ALL || INBOX_STATUS_CATEGORIES.includes(status)
    ? status
    : INBOX_STATUS_ALL;

export const getInboxProcessStateCategories = tab => processStateCategoriesByTab[tab] || {};

export const getInboxStatusStatesByProcess = (tab, status) => {
  const validStatus = getValidInboxStatus(status);
  const processCategories = getInboxProcessStateCategories(tab);

  return validStatus === INBOX_STATUS_ALL
    ? {}
    : Object.fromEntries(
        Object.entries(processCategories).map(([processName, categories]) => [
          processName,
          categories[validStatus] || [],
        ])
      );
};

const negotiationStatesForVersion = (version, status) => {
  const sharedBeforeConfirmation = [
    'inquiry',
    'quote-requested',
    'offer-pending',
    'update-pending',
    'customer-offer-pending',
    'pending-payment',
    'offer-accepted',
  ];
  const historical = {
    [INBOX_STATUS_IN_PROCESS]: [...sharedBeforeConfirmation, 'delivered', 'changes-requested'],
    [INBOX_STATUS_COMPLETED]: [
      'completed',
      'reviewed-by-customer',
      'reviewed-by-provider',
      'reviewed',
    ],
    [INBOX_STATUS_CANCELED]: [
      'canceled',
      'cancellation-refunded',
      'cancellation-paid-to-provider',
      'partially-refunded',
    ],
    [INBOX_STATUS_REJECTED]: ['request-rejected', 'offer-rejected'],
    [INBOX_STATUS_EXPIRED]: ['payment-expired'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: ['cancellation-review'],
  };
  const v1 = {
    ...historical,
    [INBOX_STATUS_CANCELED]: ['canceled'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: [],
  };
  const v3 = {
    [INBOX_STATUS_IN_PROCESS]: [
      ...sharedBeforeConfirmation,
      'provider-completed',
      'problem-reported',
      'provider-correction-completed',
    ],
    [INBOX_STATUS_COMPLETED]: [
      'delivered',
      'reviewed-by-customer',
      'reviewed-by-provider',
      'reviewed',
      'completion-paid-to-provider',
    ],
    [INBOX_STATUS_CANCELED]: [
      'canceled',
      'cancellation-refunded',
      'cancellation-paid-to-provider',
      'partially-refunded',
      'completion-refunded',
    ],
    [INBOX_STATUS_REJECTED]: ['request-rejected', 'offer-rejected'],
    [INBOX_STATUS_EXPIRED]: ['payment-expired', 'provider-completion-expired'],
    [INBOX_STATUS_ADMINISTRATIVE_REVIEW]: ['cancellation-review', 'completion-review'],
  };
  return (
    (isNegotiationRelease3CompatibleProcessVersion(NEGOTIATION_PROCESS_NAME, version)
      ? v3
      : Number(version) === 2
      ? historical
      : v1)[status] || []
  );
};

/**
 * Sharetribe combines processNames and states independently, so that query cannot express
 * process-specific state pairs. Transition names in the supported graphs are category-consistent,
 * which lets one server-side lastTransitions query preserve ordering, counts, and pagination.
 */
export const getInboxStatusLastTransitions = (tab, status) => {
  const statesByProcess = getInboxStatusStatesByProcess(tab, status);
  const transitions = Object.entries(statesByProcess).flatMap(([processName, states]) =>
    processName === NEGOTIATION_PROCESS_NAME
      ? NEGOTIATION_SUPPORTED_PROCESS_VERSIONS.flatMap(version => {
          const process = getProcess(processName, version);
          const supportedStates = negotiationStatesForVersion(version, status).filter(
            state => process.graph.states[state]
          );
          return process.getTransitionsToStates(supportedStates);
        })
      : (() => {
          const process = getProcess(processName);
          return process.getTransitionsToStates(
            states.filter(state => process.graph.states[state])
          );
        })()
  );

  return [...new Set(transitions)];
};
